import { Icon, Intent } from "@blueprintjs/core";
import { Button } from "components/buttons";
import { ShowHide } from "components/layout";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";

export function TemplateBuilderMediaCapturePreview({ question, answer, onChange }) {
    const fileRef = useRef(null);
    const [errors, setErrors] = useState("");

    const isValid = useMemo(() => {
      return errors === ""
    }, [errors]);

    const value = useMemo(() => {
        if(!answer?.answer) {
            return []
        }

        if(Array.isArray(answer.answer)) {
            return answer.answer;
        }

        return answer.answer.split(',');
    }, [answer]);

    const mediaAccept = useMemo(() => {
        const { allowImages, allowVideos} = question;
        const types = [];
        
        allowImages && types.push("image/*");
        allowVideos && types.push("video/*");
        return types.join(";");
    }, [question]);

    //Trigger validation
    useEffect(() => {
        validate(value)
    }, [value]);

    useEffect(() => {
      if (answer?.answer && answer.answer.notValid !== !isValid) {
        onChange({
          ...answer,
          notValid: !isValid,
          error: errors
        })
      }
    }, [isValid])

    const validate = useCallback((newValue = []) => {
        const { minNumberOfMedia = 0, maxNumberOfMedia, allowImages, allowVideos, required} = question;
        const length = newValue.length
      
      if ((required || !!length) && length < minNumberOfMedia) {
        setErrors(`At least ${minNumberOfMedia} is required.`);
        return;
      }

      if(!allowImages && newValue.find(i => i.type.includes("image/"))) {
        setErrors('Images are not allowed');
        return;
      }
      if(!allowVideos && newValue.find(i => i.type.includes("video/"))) {
        setErrors('Videos are not allowed');
        return;
      }

      if(maxNumberOfMedia && length > maxNumberOfMedia) {
        setErrors(`A maximum of ${maxNumberOfMedia} is allowed`);
        return;
      }

      setErrors("");
    }, []);

    const isValidMediaType = useCallback((mediaType) => {
      const { allowImages, allowVideos } = question;

      if(!allowImages && mediaType.includes("image/")) {
        return false;
      }

      return allowVideos || !mediaType.includes("video/");
    }, [question]);

    const handleChange = useCallback((newValue = []) => {
        const newAnswer = {
          ...answer,
          $answerType: "MediaCapture",
          questionId: question.id,
          answer: newValue,
          isAnswered: newValue && newValue.length > 0,
          notValid: !isValid,
          error: errors
        };

        onChange(newAnswer);
    }, [isValid]);

    const handleFile = useCallback((event) => {
        const file = event.currentTarget.files[0];

        file.id = uuid();

      handleChange([
        file,
        ...value]);
    }, [value, handleChange]);

    const handleRemove = useCallback((id) => {
      handleChange(value.filter(i => i.id !== id));
    }, [value]);      

    return <div className="check-preview-form-field">
        <div className="media-capture">
            <Button
                text="Camera"
                icon="camera"
                intent={Intent.PRIMARY}
                onClick={() => fileRef.current?.click()}
            />
            <ShowHide
              evaluator={!question.hideCameraRoll}
              show={<Button
                  text="Photo Library"
                  icon="media"
                  intent={Intent.NONE}
                  onClick={() => fileRef.current?.click()}
              />}
            />
            <input 
                ref={fileRef} 
                type="file"
                accept={mediaAccept}
                multiple={false}
                style={{ display: 'none'}}
                onChange={handleFile}
            />
            <div className="media-gallery">
                {value.map(media => (
                    <div key={media.id} className={`media-capture-item ${isValidMediaType(media.type) ? '' : 'invalid-media'}`}>
                        <img src="/logo192.png" alt="/Vision Logo" className="vision-logo" />
                        {media.type.includes('video/') && <Icon icon="play" size={56} className="media-video" />}
                        <Button
                            icon="small-cross"
                            iconOnly={true}
                            className="btn-remove"
                            onClick={() => handleRemove(media.id)}
                        />
                    </div>
                ))}
            </div>
        </div>
    </div>
}

TemplateBuilderMediaCapturePreview.propTypes = {
    answer: PropTypes.object,
    question: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired
};